import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sale } from '@/types/models';
import {
  createInvoiceShareToken,
  revokeInvoiceShareToken,
  logInvoiceDelivery,
  normalizePakistanPhone,
  fetchInvoiceShareTokens,
  fetchInvoiceDeliveryLogs,
} from '@/services/invoiceShare';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { parseDatabaseError } from '@/lib/errors';
import {
  PhoneCall,
  Mail,
  Copy,
  Check,
  Loader2,
  ExternalLink,
  Clock,
  Trash2,
  CheckCircle2,
  History,
} from 'lucide-react';

interface ShareInvoiceModalProps {
  sale: Sale;
  isOpen: boolean;
  onClose: () => void;
}

export const ShareInvoiceModal: React.FC<ShareInvoiceModalProps> = ({
  sale,
  isOpen,
  onClose,
}) => {

  const [expiryDays, setExpiryDays] = useState<number>(30);
  const [activeToken, setActiveToken] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customerPhone = sale.customer?.phone || (sale as any).customer_phone_snapshot || '';
  const customerWhatsApp = sale.customer?.whatsapp || customerPhone;
  const customerEmail = sale.customer?.email || (sale as any).customer_email_snapshot || '';
  const deliveryPreference = (sale.customer as any)?.invoice_delivery_preference || 'ask';

  const { data: shareTokens = [], refetch: refetchTokens } = useQuery({
    queryKey: ['invoice_share_tokens', sale.id],
    queryFn: () => fetchInvoiceShareTokens(sale.id),
    enabled: isOpen,
  });

  const { data: deliveryLogs = [], refetch: refetchLogs } = useQuery({
    queryKey: ['invoice_delivery_logs', sale.id],
    queryFn: () => fetchInvoiceDeliveryLogs(sale.id),
    enabled: isOpen,
  });

  // Find latest valid unrevoked token if exists
  useEffect(() => {
    const validToken = shareTokens.find(
      (t) =>
        !t.revoked_at &&
        (!t.expires_at || new Date(t.expires_at).getTime() > Date.now())
    );
    if (validToken) {
      setActiveToken(validToken.token);
    }
  }, [shareTokens]);

  const getOrCreateToken = async (): Promise<string> => {
    if (activeToken) return activeToken;

    setGenerating(true);
    try {
      const result = await createInvoiceShareToken(sale.id, expiryDays);
      setActiveToken(result.token);
      void refetchTokens();
      return result.token;
    } finally {
      setGenerating(false);
    }
  };

  const handleShareWhatsApp = async () => {
    setError(null);
    try {
      const token = await getOrCreateToken();
      const shareUrl = `${window.location.origin}/invoice/share/${token}`;
      const customerName = sale.customer?.name || (sale as any).customer_name_snapshot || 'Valued Customer';
      const phoneDigits = customerWhatsApp ? normalizePakistanPhone(customerWhatsApp) : '';

      const message = encodeURIComponent(
        `Dear ${customerName},\nThank you for choosing AL KUWAIT TECH!\n\nHere is your official digital invoice #${sale.invoice_number} (Amount: Rs. ${Number(
          sale.total
        ).toLocaleString('en-PK')}):\n${shareUrl}\n\nPlease keep this link for your warranty records.`
      );

      const waUrl = phoneDigits
        ? `https://wa.me/${phoneDigits}?text=${message}`
        : `https://wa.me/?text=${message}`;

      await logInvoiceDelivery({
        saleId: sale.id,
        channel: 'whatsapp',
        recipient: customerWhatsApp || 'Unspecified',
      });
      void refetchLogs();

      window.open(waUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(parseDatabaseError(err));
    }
  };

  const handleShareEmail = async () => {
    setError(null);
    try {
      const token = await getOrCreateToken();
      const shareUrl = `${window.location.origin}/invoice/share/${token}`;
      const customerName = sale.customer?.name || (sale as any).customer_name_snapshot || 'Customer';

      const subject = encodeURIComponent(`Invoice #${sale.invoice_number} from AL KUWAIT TECH`);
      const body = encodeURIComponent(
        `Dear ${customerName},\n\nThank you for your purchase from AL KUWAIT TECH.\n\nYou can view and print your verified digital invoice #${
          sale.invoice_number
        } (Total: Rs. ${Number(sale.total).toLocaleString('en-PK')}) here:\n${shareUrl}\n\nBest regards,\nAL KUWAIT TECH Team`
      );

      await logInvoiceDelivery({
        saleId: sale.id,
        channel: 'email',
        recipient: customerEmail || 'Unspecified',
      });
      void refetchLogs();

      window.location.href = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
    } catch (err: any) {
      setError(parseDatabaseError(err));
    }
  };

  const handleCopyLink = async () => {
    setError(null);
    try {
      const token = await getOrCreateToken();
      const shareUrl = `${window.location.origin}/invoice/share/${token}`;
      await navigator.clipboard.writeText(shareUrl);

      await logInvoiceDelivery({
        saleId: sale.id,
        channel: 'link_copy',
        recipient: 'Clipboard',
      });
      void refetchLogs();

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err: any) {
      setError(parseDatabaseError(err));
    }
  };

  const handleRevokeToken = async (token: string) => {
    if (confirm('Revoke this share link? Anyone with this URL will no longer be able to view the invoice.')) {
      try {
        await revokeInvoiceShareToken(token);
        if (activeToken === token) setActiveToken(null);
        void refetchTokens();
      } catch (err: any) {
        setError(parseDatabaseError(err));
      }
    }
  };

  return (
    <Modal isOpen={isOpen} title={`Share Invoice #${sale.invoice_number}`} onClose={onClose}>
      <div className="space-y-4 text-xs">
        {error && <ErrorAlert message={error} />}

        {/* Customer Preference Banner */}
        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-bold text-brand-black">Recipient: {sale.customer?.name || 'Walk-in Customer'}</span>
            <span className="px-2 py-0.5 bg-white border border-brand-border rounded text-[10px] font-bold uppercase text-brand-muted">
              Pref: {deliveryPreference}
            </span>
          </div>
          <div className="flex items-center space-x-3 text-[11px] text-brand-muted">
            {customerWhatsApp && <span>WhatsApp: <strong className="text-brand-black">{customerWhatsApp}</strong></span>}
            {customerPhone && customerPhone !== customerWhatsApp && <span>Phone: <strong className="text-brand-black">{customerPhone}</strong></span>}
            {customerEmail && <span>Email: <strong className="text-brand-black">{customerEmail}</strong></span>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
          <button
            type="button"
            onClick={handleShareWhatsApp}
            disabled={generating}
            className="p-3 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-xs disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
            <span>WhatsApp</span>
          </button>

          <button
            type="button"
            onClick={handleShareEmail}
            disabled={generating}
            className="p-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-xs disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            <span>Email</span>
          </button>

          <button
            type="button"
            onClick={handleCopyLink}
            disabled={generating}
            className="p-3 bg-brand-black hover:bg-neutral-800 text-white font-bold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-xs disabled:opacity-50"
          >
            {copied ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4" />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>

        {/* Share Link Settings & Active URL */}
        <div className="space-y-2 pt-2 border-t border-brand-border">
          <div className="flex items-center justify-between">
            <span className="font-bold text-brand-black text-[11px] uppercase tracking-wider">
              Share Link Configuration
            </span>
            <div className="flex items-center space-x-1.5">
              <Clock className="w-3 h-3 text-brand-muted" />
              <select
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                className="bg-brand-bg border border-brand-border rounded px-2 py-0.5 text-[10px] font-semibold"
              >
                <option value={7}>Expires in 7 days</option>
                <option value={30}>Expires in 30 days (Default)</option>
                <option value={90}>Expires in 90 days</option>
                <option value={0}>No Expiration</option>
              </select>
            </div>
          </div>

          {activeToken && (
            <div className="p-2.5 bg-green-50/70 border border-green-200 rounded-lg flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-bold text-green-800 uppercase block">Active Secure URL</span>
                <p className="font-mono text-[11px] text-green-950 truncate">
                  {window.location.origin}/invoice/share/{activeToken}
                </p>
              </div>
              <div className="flex items-center space-x-1 shrink-0">
                <a
                  href={`/invoice/share/${activeToken}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 bg-white border border-green-300 rounded text-green-800 hover:bg-green-100"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => handleRevokeToken(activeToken)}
                  className="p-1.5 bg-white border border-red-200 rounded text-brand-red hover:bg-red-50"
                  title="Revoke link"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Delivery Logs History */}
        {deliveryLogs.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-brand-border">
            <p className="font-bold text-brand-black text-[11px] uppercase tracking-wider flex items-center space-x-1">
              <History className="w-3 h-3 text-brand-green" />
              <span>Share History ({deliveryLogs.length})</span>
            </p>
            <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
              {deliveryLogs.map((log: any) => (
                <div
                  key={log.id}
                  className="p-1.5 bg-brand-bg rounded border border-brand-border flex items-center justify-between text-[10px]"
                >
                  <div className="flex items-center space-x-1.5">
                    <CheckCircle2 className="w-3 h-3 text-brand-green" />
                    <span className="font-bold capitalize">{log.channel}</span>
                    <span className="text-brand-muted">• {log.recipient}</span>
                  </div>
                  <span className="text-brand-muted font-mono">
                    {new Date(log.created_at).toLocaleTimeString('en-PK', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
